import { getCurrentUser } from "@utils/userUtils";
import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";
import { environment } from "../environments/environment";

export type AffableConnectPayload = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface AffableConnectOptions {
  endpoint?: string;
  target?: string;
  skuName?: string;
  orgName?: string;
  /**
   * Optional workflow type used when resolving DeptId from the
   * Cirules workflow‑mapping API (e.g., "Policy", "Penalty").
   * Defaults to "Policy" to preserve existing behaviour.
   */
  workflowType?: string;
  signal?: AbortSignal;
  forceRefresh?: boolean;
  /**
   * Optional department/workflow id to use when opening AffableBPM.
   * If not provided, we will try to resolve it dynamically via the
   * workflow‑mapping API, and finally fall back to the default DeptId.
   */
  deptId?: number;
}

export const AFFABLE_LICENSE_SKU_NAME = "SKU.AffableBPM.Enterprise";

const CONNECT_PATH = "/Login/ConnectWithAffableFLOW";
const EXTERNAL_RECORD_AND_FILES_PATH = "/AffableBPM/ExternalGetRecordAndFilesDetails";
const EXTERNAL_GET_FILE_BY_FILE_ID_PATH = "/AffableBPM/ExternalGetFileByFileId";
const EXTERNAL_FILE_UPLOAD_PATH = "/AffableBPM/ExternalFileUpload";
// Default department/org IDs when workflow mapping or explicit values are not provided.
// Per requirement, DeptId should fall back to 0 (not 485) when no mapping is found.
const DEFAULT_EXTERNAL_DEPT_ID = 0;
const DEFAULT_EXTERNAL_ORG_ID = 0;

const MCMAPI_BASE_URL = environment.masterConfigurationManagerURL;
const LICENSE_FEATURES_ENDPOINT = "/GetLicenseAndFeaturesForOrg";

// Cirules workflow‑mapping API (used to resolve workflow/department by org & type)
const CIRULES_API_BASE_URL = getApiOrigin();
const WORKFLOW_MAPPING_ENDPOINT = "/api/v1/workflow-mapping/by-type-and-org";

// Session storage key prefix for caching BaseURLs and orgabbrev per facility
const AFFABLE_BASEURL_CACHE_PREFIX = "affable_bpm_baseurl_";
const AFFABLE_ORGABBREV_CACHE_PREFIX = "affable_bpm_orgabbrev_";

// Session storage key prefix for caching workflow mappings per org/type
const WORKFLOW_MAPPING_CACHE_PREFIX = "affable_workflow_mapping_";

function assertBrowserEnv() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("AffableFLOW connect can only run in a browser environment.");
  }
}

interface CachedAffableData {
  baseUrl: string;
  orgabbrev: string;
}

interface CachedWorkflowMapping {
  orgName: string;
  type: string;
  workflowId: number;
  orgid?: number;
}

function getCachedAffableDataFromSession(facilityKey: string): CachedAffableData | null {
  if (typeof window === "undefined") return null;
  try {
    const baseUrlKey = `${AFFABLE_BASEURL_CACHE_PREFIX}${facilityKey}`;
    const orgabbrevKey = `${AFFABLE_ORGABBREV_CACHE_PREFIX}${facilityKey}`;
    const cachedBaseUrl = sessionStorage.getItem(baseUrlKey);
    const cachedOrgabbrev = sessionStorage.getItem(orgabbrevKey);

    // Validate that both values exist and are not empty
    if (
      cachedBaseUrl &&
      cachedOrgabbrev &&
      cachedBaseUrl.trim() !== "" &&
      cachedOrgabbrev.trim() !== ""
    ) {
      return {
        baseUrl: cachedBaseUrl.trim(),
        orgabbrev: cachedOrgabbrev.trim(),
      };
    }
    return null;
  } catch (error) {
    console.error("Error reading cached Affable data from sessionStorage:", error);
    return null;
  }
}

function getCachedWorkflowMappingFromSession(
  orgName: string,
  type: string
): CachedWorkflowMapping | null {
  if (typeof window === "undefined") return null;
  try {
    const key = `${WORKFLOW_MAPPING_CACHE_PREFIX}${orgName.trim().toLowerCase()}_${type
      .trim()
      .toLowerCase()}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedWorkflowMapping;
    if (
      parsed &&
      typeof parsed.workflowId === "number" &&
      Number.isFinite(parsed.workflowId)
    ) {
      return parsed;
    }
    return null;
  } catch (error) {
    console.error("Error reading cached workflow mapping from sessionStorage:", error);
    return null;
  }
}

function setCachedWorkflowMappingToSession(
  orgName: string,
  type: string,
  mapping: CachedWorkflowMapping
): void {
  if (typeof window === "undefined") return;
  try {
    const key = `${WORKFLOW_MAPPING_CACHE_PREFIX}${orgName.trim().toLowerCase()}_${type
      .trim()
      .toLowerCase()}`;
    sessionStorage.setItem(
      key,
      JSON.stringify({
        orgName: mapping.orgName || orgName,
        type: mapping.type || type,
        workflowId: mapping.workflowId,
        orgid: mapping.orgid,
      })
    );
  } catch (error) {
    console.error("Error saving workflow mapping to sessionStorage:", error);
  }
}

function setCachedAffableDataToSession(facilityKey: string, baseUrl: string, orgabbrev: string): void {
  if (typeof window === "undefined") return;
  try {
    const baseUrlKey = `${AFFABLE_BASEURL_CACHE_PREFIX}${facilityKey}`;
    const orgabbrevKey = `${AFFABLE_ORGABBREV_CACHE_PREFIX}${facilityKey}`;
    sessionStorage.setItem(baseUrlKey, baseUrl);
    sessionStorage.setItem(orgabbrevKey, orgabbrev);
  } catch (error) {
    console.error("Error saving Affable data to sessionStorage:", error);
  }
}

/**
 * Resolves the AffableBPM-side org_name for a given facility org_name and
 * workflow type by consulting the workflow-mapping session cache.
 * Falls back to the original orgName when no cache entry is found.
 */
function resolveAffableOrgName(facilityOrgName: string, workflowType: string): string {
  const cached = getCachedWorkflowMappingFromSession(
    facilityOrgName.trim(),
    workflowType.trim()
  );
  if (cached?.orgName && cached.orgName.trim() !== "") {
    return cached.orgName.trim();
  }
  return facilityOrgName;
}

function createHiddenForm(
  endpoint: string,
  payload: AffableConnectPayload,
  target: string
) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = endpoint;
  form.target = target;
  form.style.display = "none";

  Object.entries(payload).forEach(([key, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = value == null ? "" : String(value);
    form.appendChild(input);
  });

  return form;
}

export interface AffableLicenseFeature {
  customer_sku_id?: number;
  customer_feature_id?: number | string;
  name?: string;
  dark?: boolean;
  enabled?: boolean;
  configuration?: Array<Record<string, unknown>>;
  sku_feature_id?: number | string | null;
}

export interface AffableLicenseResponse {
  response?: {
    sku_name?: string;
    org_id?: number;
    customer_id?: number;
    org_name?: string;
    org_info?: {
      orgabbrev?: string;
      active?: boolean;
      ocrpageusage?: number;
      org_constr?: string;
      base_url?: string;
      [key: string]: unknown;
    };
    license?: Record<string, unknown>;
    features?: AffableLicenseFeature[];
    error?: string;
  };
  error?: string;
}

export interface WorkflowMappingItem {
  id: number;
  workflowId: number;
  facilityId: number;
  org_name: string;
  type: string;
  field_Mapping: unknown | null;
  orgid: number;
  record_added_flag: boolean;
}

export interface WorkflowMappingResponse {
  success: boolean;
  count: number;
  data: WorkflowMappingItem[];
}

export interface ExternalGetRecordAndFilesDetailsParams {
  orgName: string;
  skuName?: string;
  deptId?: number | string;
  orgId?: number | string;
  signal?: AbortSignal;
  payload?: Record<string, unknown>;
  perPage?: number;
  pageNumber?: number;
  /**
   * When provided, the workflow-mapping session cache is consulted to resolve
   * the AffableBPM-side org_name (which may differ from the facility org_name).
   * Defaults to "Policy" to match the connect flow.
   */
  workflowType?: string;
}

export type ExternalGetRecordAndFilesDetailsResponse = Record<string, unknown>;

export interface ExternalGetFileByFileIdParams {
  orgName: string;
  fileId: number | string;
  skuName?: string;
  deptId?: number | string;
  orgId?: number | string;
  signal?: AbortSignal;
  /** See ExternalGetRecordAndFilesDetailsParams.workflowType */
  workflowType?: string;
}

export interface ExternalGetFileByFileIdResponse {
  Status: string;
  File: {
    FileId: number;
    ImageName: string;
    ImageType: string;
    DeptID: number;
    DocumentID: number;
    FolderId: number;
    CreatedDate: string;
    Base64: string;
  };
}

export interface ExternalFileUploadParams {
  orgName: string;
  file: File | Blob;
  fileName?: string;
  documentId: number | string;
  folderId: number | string;
  skuName?: string;
  deptId?: number | string;
  orgId?: number | string;
  signal?: AbortSignal;
  commentsFile?: File | Blob;
  commentsFileName?: string;
  /** See ExternalGetRecordAndFilesDetailsParams.workflowType */
  workflowType?: string;
}

export interface ExternalFileUploadResponse {
  Status: string;
  Message?: string;
  FileId?: number;
  FileName?: string;
  SizeKB?: number;
  FileType?: string;
}

export const affablebpmIntegrationService = {
  async ConnectWithAffableFLOW(
    payload: AffableConnectPayload,
    options: AffableConnectOptions = {}
  ) {
    assertBrowserEnv();

    const normalizedOrg = options?.orgName || "";
    const workflowType = (options?.workflowType || "Policy").trim();
    let orgNameForPayload = (payload.OrgName as string) || "";

    // Step 1: Resolve DeptId via workflow-mapping API.
    // The response also contains the AffableBPM-side org_name which must be
    // used when calling GetLicenseAndFeaturesForOrg (it can differ from the
    // facility's org_name used to query the mapping endpoint).
    let resolvedDeptId: number = DEFAULT_EXTERNAL_DEPT_ID;
    let affableOrgName: string = normalizedOrg; // will be overridden from mapping response

    if (typeof options.deptId === "number" && Number.isFinite(options.deptId)) {
      resolvedDeptId = options.deptId;
    }

    if (normalizedOrg && workflowType) {
      try {
        const mapping =
          await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
            orgName: normalizedOrg,
            type: workflowType,
            signal: options.signal,
          });

        if (mapping?.success && Array.isArray(mapping.data) && mapping.data.length > 0) {
          const first = mapping.data[0];

          // Use the org_name returned by the mapping as the AffableBPM org identifier
          if (first?.org_name && typeof first.org_name === "string" && first.org_name.trim() !== "") {
            affableOrgName = first.org_name.trim();
          }

          // Only override deptId if not explicitly provided by caller
          if (!(typeof options.deptId === "number" && Number.isFinite(options.deptId))) {
            if (
              typeof first?.workflowId === "number" &&
              Number.isFinite(first.workflowId)
            ) {
              resolvedDeptId = first.workflowId;
            }
          }
        }
      } catch (error) {
        // Log but don't block the connect flow; we'll keep defaults.
        // eslint-disable-next-line no-console
        console.error("Failed to resolve workflow mapping for AffableBPM connect:", error);
      }
    }

    // Step 2: Get connect endpoint using the AffableBPM org_name from the mapping response.
    // This ensures GetLicenseAndFeaturesForOrg is called with the correct org_name.
    const endpoint =
      options.endpoint ||
      (await affablebpmIntegrationService.getConnectEndpoint({
        ...options,
        orgName: affableOrgName,
      }));

    // Step 3: Retrieve the cached orgabbrev for the AffableBPM org to use as OrgName in the payload.
    if (affableOrgName) {
      const cachedOrgabbrev =
        affablebpmIntegrationService.getCachedOrgabbrevForFacility(affableOrgName);
      if (cachedOrgabbrev && cachedOrgabbrev.trim() !== "") {
        orgNameForPayload = cachedOrgabbrev.trim();
      } else {
        throw new Error("Organization not found. Please contact support.");
      }
    }

    const finalOrgName = orgNameForPayload.trim();
    if (!finalOrgName || finalOrgName === "") {
      throw new Error("Organization not found. Please contact support.");
    }

    const updatedPayload: AffableConnectPayload = {
      ...payload,
      OrgName: finalOrgName,
      NavigateURL:
        (payload as any)?.NavigateURL || "/Records/DocViewRecords?Type=View",
      DeptId: resolvedDeptId,
    };

    const target = options.target || "_self";

    const form = createHiddenForm(endpoint, updatedPayload, target);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  },

  async getLicenseAndFeaturesForOrg(params: {
    skuName: string;
    orgName: string;
    signal?: AbortSignal;
  }): Promise<AffableLicenseResponse> {
    if (!MCMAPI_BASE_URL) {
      throw new Error(
        "APP_MASTER_CONFIGURATION_MANAGER_URL is not configured. Cannot fetch AffableFLOW license info."
      );
    }

    const url = new URL(LICENSE_FEATURES_ENDPOINT, MCMAPI_BASE_URL);
    url.searchParams.set("sku_name", params.skuName);
    url.searchParams.set("org_name", params.orgName);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      signal: params.signal,
    });

    const text = await response.text();
    const data = text ? (JSON.parse(text) as AffableLicenseResponse) : {};

    if (!response.ok) {
      const message =
        data?.response?.error ||
        data?.error ||
        `Failed to fetch AffableFLOW license info (${response.status})`;
      throw new Error(message);
    }

    if (data?.response?.error || data?.error) {
      throw new Error(data.response?.error || data.error || "Unknown error");
    }

    return data;
  },

  /**
   * Fetch workflow mapping for a given org and type (e.g., Policy) from Cirules.
   * The returned `workflowId` is used as the DeptId when calling AffableBPM.
   */
  async getWorkflowMappingByTypeAndOrg(params: {
    orgName: string;
    type: string;
    signal?: AbortSignal;
  }): Promise<WorkflowMappingResponse> {
    if (!CIRULES_API_BASE_URL) {
      throw new Error(
        "REACT_APP_CIRULES_API_BASEURL is not configured. Cannot fetch workflow mapping."
      );
    }

    const normalizedOrg = params.orgName.trim();
    const normalizedType = params.type.trim();

    // 1) Try session cache first
    const cached = getCachedWorkflowMappingFromSession(normalizedOrg, normalizedType);
    if (cached) {
      return {
        success: true,
        count: 1,
        data: [
          {
            id: 0,
            workflowId: cached.workflowId,
            facilityId: 0,
            org_name: cached.orgName,
            type: cached.type,
            field_Mapping: null,
            orgid: cached.orgid ?? 0,
            record_added_flag: false,
          },
        ],
      };
    }

    // 2) No cache, call Cirules API
    const url = new URL(WORKFLOW_MAPPING_ENDPOINT, CIRULES_API_BASE_URL);
    url.searchParams.set("type", normalizedType);
    url.searchParams.set("org_name", normalizedOrg);

    const response = await authenticatedFetch(url.toString(), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal: params.signal,
    });

    const text = await response.text();
    const data: WorkflowMappingResponse = text
      ? (JSON.parse(text) as WorkflowMappingResponse)
      : { success: false, count: 0, data: [] };

    if (!response.ok) {
      const message =
        (data as any)?.message ||
        `Failed to fetch workflow mapping (${response.status})`;
      throw new Error(message);
    }

    // 3) Store first mapping in cache for subsequent calls
    if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
      const first = data.data[0];
      if (
        typeof first?.workflowId === "number" &&
        Number.isFinite(first.workflowId)
      ) {
        setCachedWorkflowMappingToSession(normalizedOrg, normalizedType, {
          orgName: first.org_name || normalizedOrg,
          type: first.type || normalizedType,
          workflowId: first.workflowId,
          orgid: first.orgid,
        });
      }
    }

    return data;
  },

  async getRecordAndFilesDetails(
    params: ExternalGetRecordAndFilesDetailsParams
  ): Promise<ExternalGetRecordAndFilesDetailsResponse> {
    const normalizedOrgName = params.orgName?.trim();
    if (!normalizedOrgName) {
      throw new Error("orgName is required to fetch AffableBPM record details.");
    }

    // Resolve the AffableBPM-side org_name from the workflow-mapping session cache
    const workflowType = (params.workflowType || "Policy").trim();
    const affableOrgName = resolveAffableOrgName(normalizedOrgName, workflowType);

    const normalizedSku = params.skuName || AFFABLE_LICENSE_SKU_NAME;

    let affableData = getCachedAffableDataFromSession(affableOrgName);
    if (!affableData) {
      affableData = await fetchAffableData({
        skuName: normalizedSku,
        orgName: affableOrgName,
        signal: params.signal,
      });
      setCachedAffableDataToSession(
        affableOrgName,
        affableData.baseUrl,
        affableData.orgabbrev
      );
    }

    const endpoint = buildAffableEndpoint(
      affableData.baseUrl,
      EXTERNAL_RECORD_AND_FILES_PATH
    );

    const currentUser = getCurrentUser();
    const requestPayload = {
      ...(params.payload || {}),
      OrgName: affableData.orgabbrev,
      UserName: currentUser?.email || "",
      DeptId: params.deptId ?? DEFAULT_EXTERNAL_DEPT_ID,
      OrgId: params.orgId ?? DEFAULT_EXTERNAL_ORG_ID,
      perPage: params.perPage ?? 10,
      PageNumber: params.pageNumber ?? 0,
    };

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(requestPayload),
      signal: params.signal,
    });

    const text = await response.text();
    const data: ExternalGetRecordAndFilesDetailsResponse = text
      ? (JSON.parse(text) as ExternalGetRecordAndFilesDetailsResponse)
      : {};

    if (!response.ok) {
      const message =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        `Failed to fetch AffableBPM record details (${response.status})`;
      throw new Error(message);
    }

    return data;
  },

  async getFileByFileId(
    params: ExternalGetFileByFileIdParams
  ): Promise<ExternalGetFileByFileIdResponse> {
    const normalizedOrgName = params.orgName?.trim();
    if (!normalizedOrgName) {
      throw new Error("orgName is required to fetch AffableBPM file.");
    }

    if (!params.fileId) {
      throw new Error("fileId is required to fetch AffableBPM file.");
    }

    const workflowType = (params.workflowType || "Policy").trim();
    const affableOrgName = resolveAffableOrgName(normalizedOrgName, workflowType);

    const normalizedSku = params.skuName || AFFABLE_LICENSE_SKU_NAME;

    let affableData = getCachedAffableDataFromSession(affableOrgName);
    if (!affableData) {
      affableData = await fetchAffableData({
        skuName: normalizedSku,
        orgName: affableOrgName,
        signal: params.signal,
      });
      setCachedAffableDataToSession(
        affableOrgName,
        affableData.baseUrl,
        affableData.orgabbrev
      );
    }

    const endpoint = buildAffableEndpoint(
      affableData.baseUrl,
      EXTERNAL_GET_FILE_BY_FILE_ID_PATH
    );

    const currentUser = getCurrentUser();

    const formData = new URLSearchParams();
    formData.append("OrgName", affableData.orgabbrev);
    formData.append("UserName", currentUser?.email || "");
    formData.append("DeptId", String(params.deptId ?? DEFAULT_EXTERNAL_DEPT_ID));
    formData.append("OrgId", String(params.orgId ?? DEFAULT_EXTERNAL_ORG_ID));
    formData.append("FileId", String(params.fileId));

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "application/json",
      },
      body: formData.toString(),
      signal: params.signal,
    });

    const text = await response.text();
    const data: ExternalGetFileByFileIdResponse = text
      ? (JSON.parse(text) as ExternalGetFileByFileIdResponse)
      : { Status: "Error", File: {} as ExternalGetFileByFileIdResponse["File"] };

    if (!response.ok) {
      const message =
        (typeof data?.Status === "string" && data.Status !== "Success" && data.Status) ||
        `Failed to fetch AffableBPM file (${response.status})`;
      throw new Error(message);
    }

    if (data?.Status !== "Success") {
      throw new Error(data.Status || "Failed to fetch AffableBPM file");
    }

    return data;
  },

  async uploadFile(
    params: ExternalFileUploadParams
  ): Promise<ExternalFileUploadResponse> {
    const normalizedOrgName = params.orgName?.trim();
    if (!normalizedOrgName) {
      throw new Error("orgName is required to upload files to AffableFLOW.");
    }

    if (!params.file) {
      throw new Error("file is required to upload to AffableFLOW.");
    }

    if (params.documentId == null || params.folderId == null) {
      throw new Error("documentId and folderId are required to upload files.");
    }

    const workflowType = (params.workflowType || "Policy").trim();
    const affableOrgName = resolveAffableOrgName(normalizedOrgName, workflowType);

    const normalizedSku = params.skuName || AFFABLE_LICENSE_SKU_NAME;

    let affableData = getCachedAffableDataFromSession(affableOrgName);
    if (!affableData) {
      affableData = await fetchAffableData({
        skuName: normalizedSku,
        orgName: affableOrgName,
        signal: params.signal,
      });
      setCachedAffableDataToSession(
        affableOrgName,
        affableData.baseUrl,
        affableData.orgabbrev
      );
    }

    const endpoint = buildAffableEndpoint(
      affableData.baseUrl,
      EXTERNAL_FILE_UPLOAD_PATH
    );

    const currentUser = getCurrentUser();
    const formData = new FormData();
    formData.append("OrgId", String(params.orgId ?? DEFAULT_EXTERNAL_ORG_ID));
    formData.append("OrgName", affableData.orgabbrev);
    formData.append("UserName", currentUser?.email || "");
    formData.append("DeptId", String(params.deptId ?? DEFAULT_EXTERNAL_DEPT_ID));
    formData.append("DocumentId", String(params.documentId));
    formData.append("FolderId", String(params.folderId));
    let resolvedFileName = params.fileName;
    if (!resolvedFileName) {
      if (typeof File !== "undefined" && params.file instanceof File) {
        resolvedFileName = params.file.name;
      } else {
        resolvedFileName = "upload.dat";
      }
    }
    formData.append("FileData", params.file, resolvedFileName);

    const response = await fetch(endpoint, {
      method: "POST",
      body: formData,
      signal: params.signal,
    });

    const text = await response.text();
    const data: ExternalFileUploadResponse = text
      ? (JSON.parse(text) as ExternalFileUploadResponse)
      : { Status: "Error" };

    if (!response.ok) {
      const message =
        data?.Message ||
        data?.Status ||
        `Failed to upload file to AffableBPM (${response.status})`;
      throw new Error(message);
    }

    if (data.Status !== "Success") {
      throw new Error(data.Message || data.Status || "Failed to upload file to AffableBPM");
    }

    return data;
  },

  async getConnectEndpoint(options?: {
    skuName?: string;
    orgName?: string;
    signal?: AbortSignal;
    forceRefresh?: boolean;
  }): Promise<string> {
    const normalizedSku = options?.skuName || AFFABLE_LICENSE_SKU_NAME;
    const normalizedOrg = options?.orgName || "";

    // Check session storage for cached BaseURL for this specific facility
    if (!options?.forceRefresh && normalizedOrg) {
      const cachedData = getCachedAffableDataFromSession(normalizedOrg);
      if (cachedData && cachedData.baseUrl && cachedData.baseUrl.trim() !== "") {
        const endpoint = buildConnectEndpoint(cachedData.baseUrl);
        // Also update in-memory cache for backward compatibility
        setCachedConnectEndpoint(endpoint);
        return endpoint;
      }
    }

    // Fallback to in-memory cache if no orgName provided (backward compatibility)
    if (!options?.forceRefresh && !normalizedOrg) {
      const cached = getCachedConnectEndpoint();
      if (cached) return cached;
    }

    // Fetch BaseURL and orgabbrev from API
    const affableData = await fetchAffableData({
      skuName: normalizedSku,
      orgName: normalizedOrg,
      signal: options?.signal,
    });

    // Save BaseURL and orgabbrev to session storage for this facility
    if (normalizedOrg && affableData.baseUrl && affableData.orgabbrev) {
      setCachedAffableDataToSession(normalizedOrg, affableData.baseUrl, affableData.orgabbrev);
    }

    const endpoint = buildConnectEndpoint(affableData.baseUrl);
    setCachedConnectEndpoint(endpoint);
    return endpoint;
  },

  getCachedOrgabbrevForFacility(facilityKey: string): string | null {
    const cached = getCachedAffableDataFromSession(facilityKey);
    return cached?.orgabbrev || null;
  },

  clearCachedConnectEndpoint() {
    setCachedConnectEndpoint(null);
  },

  clearCachedBaseUrlForFacility(facilityKey: string) {
    if (typeof window === "undefined") return;
    try {
      const baseUrlKey = `${AFFABLE_BASEURL_CACHE_PREFIX}${facilityKey}`;
      const orgabbrevKey = `${AFFABLE_ORGABBREV_CACHE_PREFIX}${facilityKey}`;
      sessionStorage.removeItem(baseUrlKey);
      sessionStorage.removeItem(orgabbrevKey);
    } catch (error) {
      console.error("Error clearing cached Affable data from sessionStorage:", error);
    }
  },

  clearAllCachedBaseUrls() {
    if (typeof window === "undefined") return;
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.startsWith(AFFABLE_BASEURL_CACHE_PREFIX) ||
            key.startsWith(AFFABLE_ORGABBREV_CACHE_PREFIX))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      console.error("Error clearing all cached Affable data from sessionStorage:", error);
    }
  },
};

let cachedConnectEndpoint: string | null = null;
let pendingAffableDataPromise: Promise<CachedAffableData> | null = null;

function getCachedConnectEndpoint(): string | null {
  return cachedConnectEndpoint;
}

function setCachedConnectEndpoint(value: string | null) {
  cachedConnectEndpoint = value;
}

async function fetchAffableData(params: {
  skuName: string;
  orgName: string;
  signal?: AbortSignal;
}): Promise<CachedAffableData> {
  if (!pendingAffableDataPromise) {
    pendingAffableDataPromise = (async () => {
      const response =
        await affablebpmIntegrationService.getLicenseAndFeaturesForOrg({
          skuName: params.skuName,
          orgName: params.orgName,
          signal: params.signal,
        });
      const orgInfo = response?.response?.org_info;
      const baseUrl = orgInfo?.base_url;
      const orgabbrev = orgInfo?.orgabbrev;

      // Check if base_url is null, undefined, or empty
      if (!baseUrl || (typeof baseUrl === "string" && baseUrl.trim() === "")) {
        throw new Error("Base URL not found. Please contact support.");
      }

      // Check if orgabbrev is null, undefined, or empty
      if (!orgabbrev || (typeof orgabbrev === "string" && orgabbrev.trim() === "")) {
        throw new Error("Organization not found. Please contact support.");
      }

      return { baseUrl: baseUrl.trim(), orgabbrev: orgabbrev.trim() };
    })();
  }

  try {
    return await pendingAffableDataPromise;
  } finally {
    pendingAffableDataPromise = null;
  }
}

function buildConnectEndpoint(baseUrl?: string | null): string {
  return buildAffableEndpoint(baseUrl, CONNECT_PATH);
}

function buildAffableEndpoint(baseUrl?: string | null, relativePath?: string) {
  if (!baseUrl) {
    throw new Error(
      "Cannot build AffableFLOW connect endpoint without a base URL."
    );
  }
  if (!relativePath) {
    throw new Error("Cannot build AffableBPM endpoint without a relative path.");
  }
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  return `${normalizedBase}${relativePath}`;
}

